import { ReactNode } from "react";
import {
  ErrorBoundary as ReactErrorBoundary,
  FallbackProps,
} from "react-error-boundary";
import { RefreshCw } from "lucide-react";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

const DefaultFallback = ({ resetErrorBoundary }: FallbackProps) => {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen gap-4 p-6 text-center">
      <div className="flex flex-col gap-2 max-w-md">
        <h1 className="text-xl font-semibold text-charcoal">
          Something went wrong
        </h1>
        <p className="text-sm text-primary">
          An unexpected error occurred. Please try reloading the page.
        </p>
      </div>
      <button
        onClick={resetErrorBoundary}
        className="flex items-center gap-2 rounded-md bg-charcoal px-4 py-2 text-sm font-semibold text-white"
      >
        <RefreshCw size={16} />
        Try again
      </button>
    </div>
  );
};

const logError = (error: Error, info: { componentStack?: string | null }) => {
  // eslint-disable-next-line no-console
  console.error("Uncaught render error:", error, info?.componentStack);
};

const ErrorBoundary = ({ children, fallback }: ErrorBoundaryProps) => {
  if (fallback) {
    return (
      <ReactErrorBoundary fallback={fallback} onError={logError}>
        {children}
      </ReactErrorBoundary>
    );
  }

  return (
    <ReactErrorBoundary FallbackComponent={DefaultFallback} onError={logError}>
      {children}
    </ReactErrorBoundary>
  );
};

export default ErrorBoundary;
